import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Save, CheckCircle, AlertCircle, Settings as SettingsIcon, Mail, Bot, Loader2, Send, Clock, Upload, Trash2, FileText, File, CalendarClock, Printer } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import PlanningAgentRules from '@/components/settings/PlanningAgentRules';
import EquipmentProcessMatrix from '@/components/settings/EquipmentProcessMatrix';
import PlanningCalendar from '@/components/settings/PlanningCalendar';
import PlanningCalibration from '@/components/settings/PlanningCalibration';
import PlanningGeneralSettings from '@/components/settings/PlanningGeneralSettings';
import { PrintConfigImagesCard } from '@/components/PrintConfigImagesCard';

type TabType = 'integration' | 'email' | 'aibubble' | 'planning' | 'printconfig';

export default function Settings() {
  const [activeTab, setActiveTab] = useState<TabType>('integration');

  const [integrationConfig, setIntegrationConfig] = useState({
    endpointUrl: '',
    idSecurity: '',
    idClient: '',
    daysToLookBack: 2,
    syncEnabled: false,
    syncDays: [1, 2, 3, 4, 5] as number[],
    syncHour: 7,
  });

  const [emailConfig, setEmailConfig] = useState({
    emailHost: '',
    emailPort: 587,
    emailSecure: false,
    emailUser: '',
    emailPassword: '',
    emailFromName: '',
    emailFromAddress: '',
  });

  const [testEmail, setTestEmail] = useState('');

  // Agent state
  const [agentConfig, setAgentConfig] = useState({ agentName: 'QPulse Assistant', agentPersonality: '' });
  const [agentDocuments, setAgentDocuments] = useState<Array<{ id: number; file_name: string; mime_type: string; size_bytes: number; created_at: string }>>([]);
  const [isUploadingDoc, setIsUploadingDoc] = useState(false);
  const [isSavingAgent, setIsSavingAgent] = useState(false);
  const [agentSaved, setAgentSaved] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isConfigured, setIsConfigured] = useState(false);
  const [emailConfigured, setEmailConfigured] = useState(false);
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [testEmailSent, setTestEmailSent] = useState(false);

  useEffect(() => {
    loadIntegrationConfig();
    loadEmailConfig();
    loadAgentConfig();
    loadAgentDocuments();
  }, []);

  const loadIntegrationConfig = async () => {
    try {
      setIsLoadingData(true);
      setError(null);
      
      const response = await fetch('/api/production-orders/integration/config', {
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.configured) {
          setIsConfigured(true);
          setIntegrationConfig({
            endpointUrl: data.endpointUrl || '',
            idSecurity: '',
            idClient: '',
            daysToLookBack: data.daysToLookBack || 2,
            syncEnabled: data.syncEnabled ?? false,
            syncDays: Array.isArray(data.syncDays) ? data.syncDays : [1, 2, 3, 4, 5],
            syncHour: data.syncHour ?? 7,
          });
        }
      }
    } catch (error) {
      console.error('Error loading integration config:', error);
    } finally {
      setIsLoadingData(false);
    }
  };

  const loadEmailConfig = async () => {
    try {
      const response = await fetch('/api/email-config/config', {
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        const isConfigured = data.emailHost && data.emailUser;
        setEmailConfigured(isConfigured);
        setEmailConfig({
          emailHost: data.emailHost || '',
          emailPort: data.emailPort || 587,
          emailSecure: data.emailSecure || false,
          emailUser: data.emailUser || '',
          emailPassword: '',
          emailFromName: data.emailFromName || '',
          emailFromAddress: data.emailFromAddress || '',
        });
      }
    } catch (error) {
      console.error('Error loading email config:', error);
    }
  };

  const loadAgentConfig = async () => {
    try {
      const response = await fetch('/api/agent/config', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setAgentConfig({ agentName: data.agentName || 'QPulse Assistant', agentPersonality: data.agentPersonality || '' });
      }
    } catch (error) {
      console.error('Error loading agent config:', error);
    }
  };

  const loadAgentDocuments = async () => {
    try {
      const response = await fetch('/api/agent/documents', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setAgentDocuments(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error loading agent documents:', error);
    }
  };

  const handleSaveAgentConfig = async () => {
    setIsSavingAgent(true);
    try {
      const response = await fetch('/api/agent/config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ agentName: agentConfig.agentName, agentPersonality: agentConfig.agentPersonality }),
      });
      if (response.ok) {
        setAgentSaved(true);
        setTimeout(() => setAgentSaved(false), 3000);
      }
    } catch (error) {
      console.error('Error saving agent config:', error);
    } finally {
      setIsSavingAgent(false);
    }
  };

  const handleUploadDocument = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploadingDoc(true);
    setUploadSuccess(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch('/api/agent/documents', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      if (response.ok) {
        setUploadSuccess(`"${file.name}" subido correctamente`);
        setTimeout(() => setUploadSuccess(null), 4000);
        await loadAgentDocuments();
      } else {
        const err = await response.json();
        setError(err.error || 'Error al subir el documento');
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      setError('Error al subir el documento');
    } finally {
      setIsUploadingDoc(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteDocument = async (docId: number, fileName: string) => {
    if (!confirm(`¿Eliminar el documento "${fileName}"?`)) return;
    try {
      await fetch(`/api/agent/documents/${docId}`, { method: 'DELETE', credentials: 'include' });
      await loadAgentDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
    }
  };

  const handleSaveConfig = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const payload: any = {
        endpointUrl: integrationConfig.endpointUrl,
        daysToLookBack: integrationConfig.daysToLookBack,
        syncEnabled: integrationConfig.syncEnabled,
        syncDays: integrationConfig.syncDays,
        syncHour: integrationConfig.syncHour,
      };

      if (integrationConfig.idSecurity) {
        payload.idSecurity = integrationConfig.idSecurity;
      }

      if (integrationConfig.idClient) {
        payload.idClient = integrationConfig.idClient;
      }

      const response = await fetch('/api/production-orders/integration/config', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      
      if (response.ok) {
        setIsConfigured(true);
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
        
        setIntegrationConfig(prev => ({
          ...prev,
          idSecurity: '',
          idClient: '',
        }));
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Error al guardar la configuración');
      }
    } catch (error) {
      console.error('Error saving config:', error);
      setError('Error al guardar la configuración de integración');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveEmailConfig = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const payload: any = {
        emailHost: emailConfig.emailHost,
        emailPort: emailConfig.emailPort,
        emailSecure: emailConfig.emailSecure,
        emailUser: emailConfig.emailUser,
        emailPassword: emailConfig.emailPassword,
        emailFromName: emailConfig.emailFromName,
        emailFromAddress: emailConfig.emailFromAddress,
      };

      const response = await fetch('/api/email-config/config', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      
      if (response.ok) {
        setEmailConfigured(true);
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
        
        setEmailConfig(prev => ({
          ...prev,
          emailPassword: '',
        }));
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Error al guardar la configuración de email');
      }
    } catch (error) {
      console.error('Error saving email config:', error);
      setError('Error al guardar la configuración de email');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendTestEmail = async () => {
    setIsSendingTest(true);
    setError(null);
    setTestEmailSent(false);
    
    try {
      const response = await fetch('/api/email-config/send-test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ recipientEmail: testEmail }),
      });
      
      if (response.ok) {
        setTestEmailSent(true);
        setTimeout(() => setTestEmailSent(false), 5000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Error al enviar email de prueba');
      }
    } catch (error) {
      console.error('Error sending test email:', error);
      setError('Error al enviar email de prueba');
    } finally {
      setIsSendingTest(false);
    }
  };

  if (isLoadingData) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Configuración</h2>
          <p className="text-muted-foreground">
            Administra la configuración del sistema.
          </p>
        </div>
        <Card>
          <CardContent className="pt-6">
            <p className="text-muted-foreground">Cargando configuración...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold tracking-tight">Configuración</h2>
        <p className="text-muted-foreground">
          Administra la configuración del sistema.
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-md text-red-800">
          <AlertCircle className="h-5 w-5" />
          <p>{error}</p>
        </div>
      )}

      {showSuccess && (
        <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-md text-green-800">
          <CheckCircle className="h-5 w-5" />
          <p>Configuración guardada exitosamente</p>
        </div>
      )}

      {testEmailSent && (
        <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-md text-green-800">
          <CheckCircle className="h-5 w-5" />
          <p>Email de prueba enviado exitosamente. Revisa tu bandeja de entrada.</p>
        </div>
      )}

      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('integration')}
            className={`
              whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
              ${activeTab === 'integration'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            <SettingsIcon className="inline-block h-5 w-5 mr-2" />
            Integración Externa
          </button>
          <button
            onClick={() => setActiveTab('email')}
            className={`
              whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
              ${activeTab === 'email'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            <Mail className="inline-block h-5 w-5 mr-2" />
            Email SMTP
          </button>
          <button
            onClick={() => setActiveTab('aibubble')}
            className={`
              whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
              ${activeTab === 'aibubble'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            <Bot className="inline-block h-5 w-5 mr-2" />
            Agente IA
          </button>
          <button
            onClick={() => setActiveTab('planning')}
            className={`
              whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
              ${activeTab === 'planning'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            <CalendarClock className="inline-block h-5 w-5 mr-2" />
            Agente de Planeación
          </button>
          <button
            onClick={() => setActiveTab('printconfig')}
            className={`
              whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
              ${activeTab === 'printconfig'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }
            `}
          >
            <Printer className="inline-block h-5 w-5 mr-2" />
            Figuras de Configuración de Impresión
          </button>
        </nav>
      </div>

      {activeTab === 'integration' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <SettingsIcon className="h-5 w-5" />
              Integración con Servicio Externo
            </CardTitle>
            <CardDescription>
              Configure las credenciales para conectarse al servicio de órdenes de producción externo.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <label htmlFor="endpointUrl" className="block text-sm font-medium mb-2">URL del Endpoint *</label>
              <Input
                id="endpointUrl"
                type="url"
                placeholder="https://gsservice-example.com/GS025/GetOrdenesProduccion"
                value={integrationConfig.endpointUrl}
                onChange={(e) => setIntegrationConfig({ ...integrationConfig, endpointUrl: e.target.value })}
              />
              <p className="text-sm text-muted-foreground">
                URL completa del servicio de órdenes de producción
              </p>
            </div>

            <div className="space-y-2">
              <label htmlFor="idSecurity" className="block text-sm font-medium mb-2">idSecurity (Header de Seguridad)</label>
              <Input
                id="idSecurity"
                type="password"
                placeholder={isConfigured ? "••••••••••••••••••••" : "Ingrese idSecurity"}
                value={integrationConfig.idSecurity}
                onChange={(e) => setIntegrationConfig({ ...integrationConfig, idSecurity: e.target.value })}
              />
              <p className="text-sm text-muted-foreground">
                {isConfigured 
                  ? "Credencial configurada. Ingrese un nuevo valor solo si desea actualizarla."
                  : "Credencial de seguridad para el header 'idSecurity'"
                }
              </p>
            </div>

            <div className="space-y-2">
              <label htmlFor="idClient" className="block text-sm font-medium mb-2">idClient (Header de Cliente)</label>
              <Input
                id="idClient"
                type="password"
                placeholder={isConfigured ? "••••••••••••••••••••" : "Ingrese idClient"}
                value={integrationConfig.idClient}
                onChange={(e) => setIntegrationConfig({ ...integrationConfig, idClient: e.target.value })}
              />
              <p className="text-sm text-muted-foreground">
                {isConfigured 
                  ? "Credencial configurada. Ingrese un nuevo valor solo si desea actualizarla."
                  : "Credencial de cliente para el header 'idClient'"
                }
              </p>
            </div>

            <div className="space-y-2">
              <label htmlFor="daysToLookBack" className="block text-sm font-medium mb-2">Días a Buscar Hacia Atrás *</label>
              <Input
                id="daysToLookBack"
                type="number"
                min="1"
                max="365"
                placeholder="2"
                value={integrationConfig.daysToLookBack}
                onChange={(e) => setIntegrationConfig({ ...integrationConfig, daysToLookBack: parseInt(e.target.value) || 2 })}
              />
              <p className="text-sm text-muted-foreground">
                Número de días hacia atrás para buscar órdenes al importar (por defecto: 2 días)
              </p>
            </div>

            {/* Auto-sync scheduler section */}
            <div className="space-y-4 pt-4 border-t">
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-blue-500" />
                <h3 className="font-semibold text-base">Sincronización Automática</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Programa una importación automática diaria desde el servicio externo. El servidor ejecutará el proceso a la hora indicada los días seleccionados.
              </p>

              {/* Enable toggle */}
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  role="switch"
                  aria-checked={integrationConfig.syncEnabled}
                  onClick={() => setIntegrationConfig({ ...integrationConfig, syncEnabled: !integrationConfig.syncEnabled })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${
                    integrationConfig.syncEnabled ? 'bg-blue-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                      integrationConfig.syncEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
                <span className="text-sm font-medium">
                  {integrationConfig.syncEnabled ? 'Activada' : 'Desactivada'}
                </span>
              </div>

              {integrationConfig.syncEnabled && (
                <div className="space-y-4 pl-2 border-l-2 border-blue-200">
                  {/* Day selector */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium">Días de ejecución</label>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { label: 'L', value: 1, full: 'Lunes' },
                        { label: 'M', value: 2, full: 'Martes' },
                        { label: 'X', value: 3, full: 'Miércoles' },
                        { label: 'J', value: 4, full: 'Jueves' },
                        { label: 'V', value: 5, full: 'Viernes' },
                        { label: 'S', value: 6, full: 'Sábado' },
                        { label: 'D', value: 0, full: 'Domingo' },
                      ].map(({ label, value, full }) => {
                        const active = integrationConfig.syncDays.includes(value);
                        return (
                          <button
                            key={value}
                            type="button"
                            title={full}
                            onClick={() => {
                              const days = active
                                ? integrationConfig.syncDays.filter(d => d !== value)
                                : [...integrationConfig.syncDays, value];
                              setIntegrationConfig({ ...integrationConfig, syncDays: days });
                            }}
                            className={`w-9 h-9 rounded-full text-sm font-semibold border-2 transition-colors ${
                              active
                                ? 'bg-blue-600 text-white border-blue-600'
                                : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'
                            }`}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                    {integrationConfig.syncDays.length === 0 && (
                      <p className="text-xs text-amber-600">Selecciona al menos un día para activar la sincronización.</p>
                    )}
                  </div>

                  {/* Hour selector */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium">Hora de ejecución</label>
                    <select
                      value={integrationConfig.syncHour}
                      onChange={e => setIntegrationConfig({ ...integrationConfig, syncHour: parseInt(e.target.value) })}
                      className="block w-48 rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {Array.from({ length: 24 }, (_, h) => {
                        const label = h === 0 ? '12:00 AM (medianoche)' : h < 12 ? `${h}:00 AM` : h === 12 ? '12:00 PM (mediodía)' : `${h - 12}:00 PM`;
                        return <option key={h} value={h}>{String(h).padStart(2, '0')}:00 — {label}</option>;
                      })}
                    </select>
                    <p className="text-xs text-muted-foreground">
                      La sincronización se ejecutará a las {String(integrationConfig.syncHour).padStart(2, '0')}:00 h (hora del servidor).
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-4 pt-4 border-t">
              <Button 
                onClick={handleSaveConfig} 
                disabled={isLoading || !integrationConfig.endpointUrl}
              >
                {isLoading ? (
                  <>Guardando...</>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Guardar Configuración
                  </>
                )}
              </Button>
            </div>

            {isConfigured && (
              <div className="flex items-center gap-2 p-4 bg-blue-50 border border-blue-200 rounded-md text-blue-800">
                <CheckCircle className="h-5 w-5" />
                <p className="text-sm">
                  La integración está configurada. Las credenciales se almacenan de forma segura y no se muestran por razones de seguridad.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 'email' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Configuración de Email SMTP
            </CardTitle>
            <CardDescription>
              Configure el servidor SMTP para envío de correos electrónicos desde la aplicación.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label htmlFor="emailHost" className="block text-sm font-medium mb-2">Servidor SMTP *</label>
                <Input
                  id="emailHost"
                  type="text"
                  placeholder="smtp.gmail.com"
                  value={emailConfig.emailHost}
                  onChange={(e) => setEmailConfig({ ...emailConfig, emailHost: e.target.value })}
                />
                <p className="text-sm text-muted-foreground">
                  Ejemplos: smtp.gmail.com, smtp-mail.outlook.com
                </p>
              </div>

              <div className="space-y-2">
                <label htmlFor="emailPort" className="block text-sm font-medium mb-2">Puerto SMTP *</label>
                <Input
                  id="emailPort"
                  type="number"
                  placeholder="587"
                  value={emailConfig.emailPort}
                  onChange={(e) => setEmailConfig({ ...emailConfig, emailPort: parseInt(e.target.value) || 587 })}
                />
                <p className="text-sm text-muted-foreground">
                  Común: 587 (STARTTLS), 465 (SSL), 25
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="emailUser" className="block text-sm font-medium mb-2">Usuario SMTP *</label>
              <Input
                id="emailUser"
                type="text"
                placeholder="tu-email@ejemplo.com"
                value={emailConfig.emailUser}
                onChange={(e) => setEmailConfig({ ...emailConfig, emailUser: e.target.value })}
              />
              <p className="text-sm text-muted-foreground">
                Generalmente es tu dirección de correo electrónico
              </p>
            </div>

            <div className="space-y-2">
              <label htmlFor="emailPassword" className="block text-sm font-medium mb-2">Contraseña SMTP</label>
              <Input
                id="emailPassword"
                type="password"
                placeholder={emailConfigured ? "••••••••••••••••••••" : "Ingrese contraseña"}
                value={emailConfig.emailPassword}
                onChange={(e) => setEmailConfig({ ...emailConfig, emailPassword: e.target.value })}
              />
              <p className="text-sm text-muted-foreground">
                {emailConfigured 
                  ? "Contraseña configurada. Ingrese un nuevo valor solo si desea actualizarla."
                  : "Para Gmail, usa una contraseña de aplicación"
                }
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label htmlFor="emailFromAddress" className="block text-sm font-medium mb-2">Email Remitente *</label>
                <Input
                  id="emailFromAddress"
                  type="email"
                  placeholder="notificaciones@tuempresa.com"
                  value={emailConfig.emailFromAddress}
                  onChange={(e) => setEmailConfig({ ...emailConfig, emailFromAddress: e.target.value })}
                />
                <p className="text-sm text-muted-foreground">
                  Email que aparecerá como remitente
                </p>
              </div>

              <div className="space-y-2">
                <label htmlFor="emailFromName" className="block text-sm font-medium mb-2">Nombre Remitente</label>
                <Input
                  id="emailFromName"
                  type="text"
                  placeholder="QPulseMES"
                  value={emailConfig.emailFromName}
                  onChange={(e) => setEmailConfig({ ...emailConfig, emailFromName: e.target.value })}
                />
                <p className="text-sm text-muted-foreground">
                  Nombre que aparecerá como remitente
                </p>
              </div>
            </div>

            <div className="flex gap-4 pt-4 border-t">
              <Button 
                onClick={handleSaveEmailConfig} 
                disabled={isLoading || !emailConfig.emailHost || !emailConfig.emailUser || !emailConfig.emailFromAddress}
              >
                {isLoading ? (
                  <>Guardando...</>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Guardar Configuración
                  </>
                )}
              </Button>
            </div>

            {emailConfigured && (
              <>
                <div className="flex items-center gap-2 p-4 bg-blue-50 border border-blue-200 rounded-md text-blue-800">
                  <CheckCircle className="h-5 w-5" />
                  <p className="text-sm">
                    La configuración de email está guardada. Las credenciales se almacenan de forma segura.
                  </p>
                </div>

                <div className="space-y-4 pt-4 border-t">
                  <h3 className="font-medium">Enviar Email de Prueba</h3>
                  <div className="flex gap-4">
                    <Input
                      type="email"
                      placeholder="correo@ejemplo.com"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      className="flex-1"
                    />
                    <Button 
                      onClick={handleSendTestEmail} 
                      disabled={isSendingTest || !testEmail}
                    >
                      {isSendingTest ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Enviando...
                        </>
                      ) : (
                        <>
                          <Send className="mr-2 h-4 w-4" />
                          Enviar
                        </>
                      )}
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Envía un email de prueba para verificar que la configuración funciona correctamente.
                  </p>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 'planning' && (
        <div className="space-y-6">
          <PlanningGeneralSettings />
          <PlanningCalendar />
          <PlanningAgentRules />
          <EquipmentProcessMatrix />
          <PlanningCalibration />
        </div>
      )}

      {activeTab === 'printconfig' && <PrintConfigImagesCard />}

      {activeTab === 'aibubble' && (
        <div className="space-y-6">
          {/* Agent saved notification */}
          {agentSaved && (
            <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-md text-green-800">
              <CheckCircle className="h-5 w-5" />
              <p>Configuración del agente guardada</p>
            </div>
          )}
          {uploadSuccess && (
            <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-md text-green-800">
              <CheckCircle className="h-5 w-5" />
              <p>{uploadSuccess}</p>
            </div>
          )}

          {/* Agent Identity Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bot className="h-5 w-5 text-blue-600" />
                Identidad del Agente
              </CardTitle>
              <CardDescription>
                Define el nombre y la personalidad del asistente de IA que aparece en la burbuja de chat de la aplicación.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-2">
                <label className="block text-sm font-medium">Nombre del Agente</label>
                <Input
                  placeholder="QPulse Assistant"
                  value={agentConfig.agentName}
                  onChange={(e) => setAgentConfig({ ...agentConfig, agentName: e.target.value })}
                  maxLength={100}
                />
                <p className="text-xs text-muted-foreground">Este nombre aparecerá en la cabecera del chat y como presentación al usuario.</p>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium">Personalidad y Estilo de Respuesta</label>
                <textarea
                  className="w-full min-h-[140px] p-3 border rounded-md text-sm resize-y focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={`Ejemplos:\n- Responde siempre de forma concisa y técnica, enfocado en manufactura.\n- Soy un asistente formal y preciso que siempre cita la fuente de los datos.\n- Respondo de manera amigable y uso ejemplos prácticos del día a día en planta.`}
                  value={agentConfig.agentPersonality}
                  onChange={(e) => setAgentConfig({ ...agentConfig, agentPersonality: e.target.value })}
                  maxLength={2000}
                />
                <p className="text-xs text-muted-foreground">
                  Instrucciones adicionales sobre cómo debe comunicarse el agente. Complementa el conocimiento base del sistema.
                </p>
              </div>

              <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-xs text-blue-800">
                  <strong>Conocimiento incluido automáticamente:</strong> El agente ya tiene información completa sobre todos los módulos de QPulseMES, incluyendo las fórmulas exactas del Dashboard (OEE, volúmenes, carga por máquina) e Indicadores (Kg Flexo, % Desperdicio, Arranques, PT). No necesitas describirlos en la personalidad.
                </p>
              </div>

              <div className="pt-2">
                <Button onClick={handleSaveAgentConfig} disabled={isSavingAgent || !agentConfig.agentName.trim()}>
                  {isSavingAgent ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Guardando...</>
                  ) : (
                    <><Save className="mr-2 h-4 w-4" />Guardar Configuración</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Knowledge Documents Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-purple-600" />
                Documentos de Conocimiento
              </CardTitle>
              <CardDescription>
                Sube PDFs o archivos Markdown (.md) con políticas internas, procedimientos, especificaciones técnicas u otra información que el agente deba conocer.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Upload button */}
              <div className="flex items-center gap-4">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.md,.txt,text/markdown,text/plain,application/pdf"
                  className="hidden"
                  onChange={handleUploadDocument}
                />
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploadingDoc}
                  className="border-dashed border-2"
                >
                  {isUploadingDoc ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Subiendo...</>
                  ) : (
                    <><Upload className="mr-2 h-4 w-4" />Subir documento</>
                  )}
                </Button>
                <p className="text-sm text-muted-foreground">PDF, Markdown (.md) o texto (.txt) · Máx. 10 MB</p>
              </div>

              {/* Documents list */}
              {agentDocuments.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground text-sm border border-dashed rounded-lg">
                  <File className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                  <p>No hay documentos cargados.</p>
                  <p className="text-xs mt-1">Sube archivos PDF o Markdown para ampliar el conocimiento del agente.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {agentDocuments.map((doc) => (
                    <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 border rounded-lg">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-8 h-8 rounded flex items-center justify-center flex-shrink-0 ${
                          doc.mime_type === 'application/pdf' ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'
                        }`}>
                          <FileText className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{doc.file_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {doc.mime_type === 'application/pdf' ? 'PDF' : 'Texto'} ·{' '}
                            {doc.size_bytes > 1024 * 1024
                              ? `${(doc.size_bytes / 1024 / 1024).toFixed(1)} MB`
                              : `${Math.round(doc.size_bytes / 1024)} KB`} ·{' '}
                            {new Date(doc.created_at).toLocaleDateString('es-MX')}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeleteDocument(doc.id, doc.file_name)}
                        className="p-1.5 text-gray-400 hover:text-red-500 transition-colors rounded flex-shrink-0"
                        title="Eliminar documento"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
                <p className="text-xs text-amber-800">
                  <strong>¿Cuándo usar documentos?</strong> Útil para: políticas de calidad ISO, procedimientos de operación estándar (POEs), especificaciones técnicas de materiales, guías de seguridad, manuales internos. El agente incluirá esta información al responder preguntas relevantes.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
